import {
  type EntityTypeEnumType,
  EntityTypeEnumValues,
} from "@shared-database/enums";

/** Dimensions of the stored pgvector embedding; must match column + HNSW index. */
export const EMBEDDING_DIMENSIONS = 1536;

export const EMBEDDING_MODEL = "text-embedding-3-small";

/** Per-request cap for the Azure OpenAI embeddings endpoint. */
export const EMBEDDING_MAX_BATCH_SIZE = 96;

/**
 * How many embedding batches run in parallel. OpenAI rate limits are
 * per-minute (TPM/RPM), not per-request, so a small burst of concurrent
 * batches improves bulk-ingest throughput without changing the total
 * token cost. Keep bounded so a single large file doesn't exhaust the
 * tenant quota for interactive query-time embeds.
 */
export const EMBEDDING_BATCH_CONCURRENCY = 3;

/** Canonical tiktoken encoder for the text-embedding-3 family. */
export const TIKTOKEN_ENCODING = "cl100k_base";

/**
 * Approximate target token count per chunk, with a small overlap to preserve
 * context across chunk boundaries.
 */
export const CHUNK_TOKEN_SIZE = 1000;
export const CHUNK_TOKEN_OVERLAP = 200;

// ---------------------------------------------------------------------------
// Graph extraction tunables
// ---------------------------------------------------------------------------

/**
 * Bump whenever the graph extraction prompt/schema/post-processing semantics
 * change in a way that makes cached content-level extraction unsafe to reuse.
 */
export const GRAPH_EXTRACTION_CACHE_VERSION = "profile-graph-extraction-v1";

/** Chunks per LLM extraction call. Larger batches amortise prompt overhead but hurt recall. */
export const MAX_CHUNKS_PER_LLM_CALL = 3;

/** Max parallel extraction LLM calls per file. Azure retry policy handles TPM throttling. */
export const LLM_CONCURRENCY = 8;

/** Per-call extraction LLM timeout. Failed batches are retried once sequentially. */
export const GRAPH_LLM_CALL_TIMEOUT_MS = 120_000;

/**
 * If a batch returns at or above this number of entities we run one more
 * "gleaning" LLM call asking for missed entities. Wider graph-RAG writeups
 * often report measurable recall improvements from this at modest cost.
 */
export const GLEANING_SATURATION_THRESHOLD = 10;

/**
 * Max "already-extracted" seed items sent back to the gleaning LLM
 * pass. Truncated to keep the gleaning prompt small — the model only
 * needs enough context to avoid duplicating the first pass, not a
 * full transcript of it.
 */
export const GLEAN_SEED_MAX_ITEMS = 60;

/**
 * Hard cap on the number of gleaning passes we run per batch.
 * Some reference pipelines loop gleaning until the LLM self-reports
 * "finished"; we cap it to avoid unbounded cost on adversarial inputs
 * that keep emitting marginal entities. Each pass costs one LLM call
 * and is only taken when the previous pass actually added new
 * entities, so the loop is self-terminating in the common case.
 */
export const GLEANING_MAX_PASSES = 3;

/**
 * Minimum total character count across all chunks in a batch before gleaning
 * is considered. Short texts (single sentences, abstracts) are fully extracted
 * in a single pass; gleaning them only produces noise.
 */
export const GLEANING_MIN_TEXT_LENGTH = 600;

/**
 * Max entities per LLM filter validation batch. Larger batches amortise
 * prompt overhead; smaller batches give the LLM more attention per item.
 */
export const FILTER_LLM_BATCH_SIZE = 25;

/** Max rows per DB INSERT/UPSERT batch during graph persistence. */
export const DB_INSERT_BATCH_SIZE = 200;

/**
 * Minimum confidence the LLM must have in a "remove" verdict for it to
 * take effect. Conservative: uncertain removals default to keeping the
 * entity, protecting recall at the cost of a few false positives.
 */
export const FILTER_REMOVE_CONFIDENCE_THRESHOLD = 0.7;

/**
 * Hard cap on the entity-name string the extraction LLM is allowed to
 * return. Anything longer is almost certainly a hallucinated span of
 * text, and the DB column + index benefit from a bounded length.
 */
export const ENTITY_NAME_MAX_LEN = 256;

/**
 * Cap on a single extracted entity's per-mention description. Longer
 * descriptions get truncated before merging so one verbose chunk
 * can't dominate the longest-wins pick.
 */
export const ENTITY_DESCRIPTION_MAX_LEN = 1_500;

/**
 * Max entities a single extraction LLM call may return. Matches the
 * {@link ExtractionResultSchema} zod `.max()` — keeps the response
 * small enough to fit Azure's structured-output budget and protects
 * downstream merge loops from pathological batches.
 */
export const EXTRACTION_MAX_ENTITIES = 80;

/** Max relations per extraction call. See {@link EXTRACTION_MAX_ENTITIES}. */
export const EXTRACTION_MAX_RELATIONS = 120;

/**
 * Entity type vocabulary the graph extractor asks the LLM to use. Re-exported
 * from {@link EntityTypeEnumValues} so the Postgres enum, the LLM prompt, the
 * Zod schema and the UI palette all share one source of truth — but importers
 * of the labels/colours don't transitively pull in the Azure AI SDK.
 */
export const ENTITY_TYPES = EntityTypeEnumValues;
export type EntityType = EntityTypeEnumType;

/** Characters of entity description fed into the entity embedding text. */
export const ENTITY_DESCRIPTION_EMBEDDING_CHAR_LIMIT = 500;

/** Characters of relation description fed into the relation embedding text. */
export const RELATION_DESCRIPTION_EMBEDDING_CHAR_LIMIT = 400;

/**
 * Cap on the combined (pre-join) description we surface per graph edge in
 * the knowledge-graph router. Prevents one overly verbose edge from
 * blowing the client payload when many recurring relations concatenate.
 */
export const GRAPH_EDGE_DESCRIPTION_CHAR_LIMIT = 2_000;

/**
 * Upper bound for the aggregated weight displayed on a single graph edge.
 * The underlying LLM scoring is already bounded per relation row, but the
 * knowledge-graph router collapses multiple relations with the same
 * (source, target, type) by summing weights, which can produce edges
 * that dominate the renderer's thickness scale on documents with many
 * repeated mentions. Capping the aggregate keeps the visualisation
 * readable without losing the underlying relation rows.
 */
export const GRAPH_EDGE_MAX_AGGREGATE_WEIGHT = 10;

/**
 * Below this threshold we keep the longest-wins shortcut: running the LLM
 * on two short snippets doesn't buy a meaningfully better synthesis, and
 * the round-trip dominates extraction time.
 */
export const ENTITY_SUMMARY_MIN_CHARS = 200;

/** Max characters we feed into the summariser; keeps prompt cost bounded. */
export const ENTITY_SUMMARY_MAX_INPUT_CHARS = 1_500;

/** Cap the synthesised description so the DB column and embedding stay bounded. */
export const ENTITY_SUMMARY_OUTPUT_MAX_CHARS = 1_500;

/**
 * Heuristic thresholds for {@link shouldReplaceEntityDescription}. A
 * stored description is replaced when the candidate is meaningfully
 * more informative than the existing one — defined as either
 * (a) the existing text is shorter than `MIN_LEN`, in which case any
 * longer candidate wins; (b) the candidate is at least `GROWTH_FACTOR`
 * times longer; or (c) the candidate adds at least `ABS_DELTA_CHARS`
 * characters. Values tuned to avoid thrashing the HNSW cache on
 * cosmetic rewordings while still capturing real content growth.
 */
export const ENTITY_DESCRIPTION_REPLACE_MIN_LEN = 40;
export const ENTITY_DESCRIPTION_REPLACE_GROWTH_FACTOR = 1.2;
export const ENTITY_DESCRIPTION_REPLACE_ABS_DELTA_CHARS = 120;

/**
 * Cap on the per-relation description written to the DB. Keeps a
 * single wordy edge from blowing out the column and the downstream
 * embedding text. Extraction-time guardrail; the
 * UI-facing {@link GRAPH_EDGE_DESCRIPTION_CHAR_LIMIT} caps the
 * aggregated payload instead.
 */
export const RELATION_DESCRIPTION_DB_CHAR_LIMIT = 1_000;

/**
 * Upper bound on the LLM-emitted per-relation weight (and lower bound
 * is implicitly 0). The extraction prompt asks for an integer in 0-10;
 * we still clamp here so a misbehaving model can't poison the
 * topology weighting downstream.
 */
export const RELATION_WEIGHT_MAX = 10;

/**
 * Hard cap on the canonical relation-type slug stored in the DB. The
 * extraction LLM occasionally emits free-form prose for the type; the
 * normaliser snake-cases it but never trims, so we apply this cap as
 * a final safety net to keep the index small and the legend readable.
 */
export const RELATION_TYPE_MAX_LEN = 80;

/** Cap on stored profile graph error text so verbose stacks do not bloat rows. */
export const GRAPH_ERROR_MESSAGE_CHAR_LIMIT = 1_000;
